import { useQuery } from "@tanstack/react-query";
import type React from "react";

import type { AdoptionRateDetailsDto, AdoptionRateDto } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { useSlug } from "@/hooks/useSlug";
import { DonutChart } from "@/modules/analytics/components/DonutChart";
import { ChartLegend } from "@/modules/analytics/components/utility/ChartLegend";
import { dataColors } from "@/modules/analytics/theme";
import { useAnalyticsQueries } from "@/queries/analytics";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { AnalyticsWidgetCard } from "../AnalyticsWidgetCard";

const CHART_SIZE = 160;
const FALLBACK_ADOPTION_STATS: AdoptionRateDto = {
  adoptionRatePercentage: 0,
  totalAddresses: 0,
  totalAddressesInvited: 0,
  invitedAddressesActivated: 0,
  invitedAddressesNotActivated: 0,
  unknownStatusAddresses: 0,
  totalResidents: 0,
};
const FALLBACK_BENCHMARK_PERCENTAGE = 0;
const FALLBACK_PERIOD: AdoptionRateDetailsDto["period"] = "zeroToOneMonth";

export function AdoptionWidget(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();

  const analyticsQueries = useAnalyticsQueries();

  const {
    data: adoption,
    isPending: isPendingAdoption,
    isError: isErrorAdoption,
    error: errorAdoption,
    refetch: refetchAdoption,
  } = useQuery(analyticsQueries.getAdoption());
  const {
    data: adoptionDetails,
    isPending: isPendingAdoptionDetails,
    isError: isErrorAdoptionDetails,
    error: errorAdoptionDetails,
    refetch: refetchAdoptionDetails,
  } = useQuery(analyticsQueries.getAdoptionDetails("last6Months"));

  const handleReload = () => {
    void refetchAdoption();
    void refetchAdoptionDetails();
  };

  const isLoading = isPendingAdoption || isPendingAdoptionDetails;
  const isError = isErrorAdoption || isErrorAdoptionDetails;
  const error = errorAdoption || errorAdoptionDetails;

  const primaryData = adoption || FALLBACK_ADOPTION_STATS;
  const benchmarkPercentage =
    adoptionDetails?.benchmark[adoptionDetails?.benchmark.length - 1]?.percentage || FALLBACK_BENCHMARK_PERCENTAGE;
  const period = adoptionDetails?.period || FALLBACK_PERIOD;
  const isDataAvailable = primaryData.totalAddressesInvited > 0 && primaryData.totalAddresses > 0;
  const isActionRequired =
    !isLoading && !isError && isDataAvailable && primaryData.adoptionRatePercentage < benchmarkPercentage;

  return (
    <AnalyticsWidgetCard.Root
      title={t("component.analytics-widget.adoption.title")}
      href={isDataAvailable ? routes.analytics.adoptionDetails({ slug }) : undefined}
      isLoading={isLoading}
      isError={isError}
      isDataAvailable={isDataAvailable}
      error={error}
      onReload={handleReload}
    >
      <AnalyticsWidgetCard.Body>
        <DonutChart size={CHART_SIZE} value={primaryData.adoptionRatePercentage} />
        <ChartLegend
          items={[
            {
              label: t("component.analytics-widget.adoption.chart.legend.primary", {
                count: primaryData.invitedAddressesActivated,
              }),
              color: dataColors.primary,
            },
          ]}
        />
      </AnalyticsWidgetCard.Body>
      <AnalyticsWidgetCard.Footer>
        {isActionRequired && (
          <Button
            type="link"
            state={{
              filters: {
                IncludeDeleted: false,
                IncludeAdmins: false,
                IncludeInvited: true,
                IncludeRegistered: false,
              } satisfies ApiQueryParams<"getUsersV1">,
            }}
            href={routes.users.list({ slug })}
          >
            {t("component.analytics-widget.adoption.action-btn")}
          </Button>
        )}

        <AnalyticsWidgetCard.ReferenceLabel
          currValue={primaryData.adoptionRatePercentage}
          referenceValue={benchmarkPercentage}
        />
        <AnalyticsWidgetCard.BenchmarkInfo benchmark={benchmarkPercentage} period={period} />
      </AnalyticsWidgetCard.Footer>
    </AnalyticsWidgetCard.Root>
  );
}
